import {
  bytesToArrayBuffer,
  canonicalJson,
  fromBase64Url,
  textEncoder,
} from "./protocol";
import { VERIFIED_EMAIL_CREDENTIAL_TYPE } from "./attester";

export const SELF_ISSUED_CREDENTIAL_TYPE = "vibe.credential.self-issued.v1" as const;
export const SELF_ISSUED_CREDENTIAL_DOMAIN = "vibe.credential.self-issued.v1";

export const CREDENTIAL_CLAIM_VOCABULARY_V1 = {
  email: {
    valueType: "string",
    credentialTypes: [VERIFIED_EMAIL_CREDENTIAL_TYPE],
  },
  name: {
    valueType: "string",
    credentialTypes: [SELF_ISSUED_CREDENTIAL_TYPE],
  },
} as const;

export type CredentialClaimNameV1 = keyof typeof CREDENTIAL_CLAIM_VOCABULARY_V1;

export function credentialClaimAllowedForType(claim: string, credentialType: string): claim is CredentialClaimNameV1 {
  const definition = CREDENTIAL_CLAIM_VOCABULARY_V1[claim as CredentialClaimNameV1];
  return Boolean(definition?.credentialTypes.some((type) => type === credentialType));
}

export function normalizeSelfIssuedNameClaim(value: string): string {
  const normalized = value.normalize("NFC").trim().replace(/\s+/gu, " ");
  if (!normalized || [...normalized].length > 120 || /[\u0000-\u001f\u007f]/u.test(normalized)) {
    throw new TypeError("Enter a name between 1 and 120 characters.");
  }
  return normalized;
}

export interface CredentialSignatureV1 {
  algorithm: "ES256";
  encoding: "p1363";
  keyId: string;
  value: string;
}

export interface SelfIssuedCredentialPayloadV1 {
  version: 1;
  type: typeof SELF_ISSUED_CREDENTIAL_TYPE;
  id: string;
  issuerDid: string;
  subjectDid: string;
  claims: Record<string, string>;
  issuedAt: string;
}

export interface SelfIssuedCredentialV1 extends SelfIssuedCredentialPayloadV1 {
  signature: CredentialSignatureV1;
}

export interface WalletCredentialSummary {
  id: string;
  type: string;
  issuerDid: string;
  subjectDid: string;
  claims: Record<string, string>;
  issuedAt: string;
  expiresAt: string | null;
  verification: "self_signed" | "issuer_signed";
}

export interface CredentialPresentationV1 {
  version: 1;
  audience: string;
  subjectDid: string;
  issuedAt: string;
  requirementsDigest: string;
  disclosures: Array<{
    requirementId: string;
    credentialId: string;
    claims: Record<string, string>;
    credential: unknown;
  }>;
}

export function selfIssuedCredentialPayload(
  credential: SelfIssuedCredentialV1 | SelfIssuedCredentialPayloadV1,
): SelfIssuedCredentialPayloadV1 {
  return {
    version: 1,
    type: SELF_ISSUED_CREDENTIAL_TYPE,
    id: credential.id,
    issuerDid: credential.issuerDid,
    subjectDid: credential.subjectDid,
    claims: { ...credential.claims },
    issuedAt: credential.issuedAt,
  };
}

export function selfIssuedCredentialSigningInput(
  credential: SelfIssuedCredentialV1 | SelfIssuedCredentialPayloadV1,
): string {
  return `${SELF_ISSUED_CREDENTIAL_DOMAIN}\n${canonicalJson(selfIssuedCredentialPayload(credential) as never)}`;
}

export async function verifySelfIssuedCredential(
  credential: SelfIssuedCredentialV1,
  subjectPublicJwk: JsonWebKey,
): Promise<boolean> {
  try {
    if (
      credential.version !== 1 ||
      credential.type !== SELF_ISSUED_CREDENTIAL_TYPE ||
      credential.issuerDid !== credential.subjectDid ||
      credential.signature.algorithm !== "ES256" ||
      credential.signature.encoding !== "p1363" ||
      credential.signature.keyId !== `${credential.subjectDid}#root`
    ) return false;
    const key = await crypto.subtle.importKey(
      "jwk",
      subjectPublicJwk,
      { name: "ECDSA", namedCurve: "P-256" },
      false,
      ["verify"],
    );
    return crypto.subtle.verify(
      { name: "ECDSA", hash: "SHA-256" },
      key,
      bytesToArrayBuffer(fromBase64Url(credential.signature.value)),
      textEncoder.encode(selfIssuedCredentialSigningInput(credential)),
    );
  } catch {
    return false;
  }
}
